import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService, UserDto } from '../../../services/auth/auth';
import { PushNotificationService } from '../../../services/push-notification';
import { LanguageService } from '../../../services/i18n/language';
import { CustomsCalculationsService } from '../../../services/api/customs-calculations';
import { Subscription } from 'rxjs';
import { RolePickerComponent } from '../role-picker/role-picker.component';

/** Тот же ключ, что в SupplierInviteComponent (PENDING_SUPPLIER_INVITE_KEY) — сюда
 *  попадает токен приглашения, если поставщик выбрал «У меня уже есть аккаунт». */
const PENDING_SUPPLIER_INVITE_KEY = 'pendingSupplierInviteToken';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, RolePickerComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  loading = false;
  error: string | null = null;

  // Переводы
  titleText = 'Вход в систему';
  subtitleText = 'Введите ваши учетные данные для входа';
  usernameLabel = 'Email';
  usernamePlaceholder = 'Введите email';
  passwordLabel = 'Пароль';
  passwordPlaceholder = 'Введите пароль';
  submitText = 'Войти';
  submittingText = 'Вход...';
  noAccountText = 'Нет аккаунта?';
  registerText = 'Зарегистрироваться';
  rolePickerOpen = false;
  requiredText = '*';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private pushService: PushNotificationService,
    private router: Router,
    private languageService: LanguageService,
    private customsCalculationsService: CustomsCalculationsService,
    private cdr: ChangeDetectorRef
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(50)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]]
    });
  }

  ngOnInit(): void {
    // Если уже авторизован, перенаправляем на соответствующую страницу по роли
    if (this.authService.isAuthenticated()) {
      this.redirectByRole();
      return;
    }

    this.updateTranslations();

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** «Зарегистрироваться» открывает выбор роли вместо сразу формы грузовладельца. */
  openRolePicker(event: Event): void {
    event.preventDefault();
    this.rolePickerOpen = true;
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('login.title');
    this.subtitleText = this.languageService.translate('login.subtitle');
    this.usernameLabel = this.languageService.translate('login.username');
    this.usernamePlaceholder = this.languageService.translate('login.usernamePlaceholder');
    this.passwordLabel = this.languageService.translate('login.password');
    this.passwordPlaceholder = this.languageService.translate('login.passwordPlaceholder');
    this.submitText = this.languageService.translate('login.submit');
    this.submittingText = this.languageService.translate('login.submitting');
    this.noAccountText = this.languageService.translate('login.noAccount');
    this.registerText = this.languageService.translate('login.register');
    this.requiredText = this.languageService.translate('registration.cargoOwner.required');
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.loginForm.value;
    const request = {
      username: formValue.username,
      password: formValue.password
    };

    this.authService.signIn(request).subscribe({
      next: (response) => {
        this.loading = false;
        console.log('Login successful:', { token: response.token ? 'present' : 'missing', user: response.user });
        // Подписываемся на push-уведомления (без ожидания)
        this.pushService.subscribeAndRegister().catch(() => {});

        // Если перед логином был выбор «У меня уже есть аккаунт» на странице
        // приглашения поставщика — принимаем это приглашение теперь, вместо обычного
        // редиректа по роли.
        const pendingInviteToken = sessionStorage.getItem(PENDING_SUPPLIER_INVITE_KEY);
        if (pendingInviteToken && response.user?.role?.code === 'ROLE_SUPPLIER') {
          sessionStorage.removeItem(PENDING_SUPPLIER_INVITE_KEY);
          this.customsCalculationsService.acceptSupplierInvite(pendingInviteToken).subscribe({
            next: () => this.router.navigate(['/supplier/dashboard']),
            error: (err) => {
              this.error = err.error?.message || 'Не удалось принять приглашение';
              this.cdr.markForCheck();
            }
          });
          return;
        }
        if (pendingInviteToken) {
          // Вошёл под другой ролью — приглашение для поставщиков, тут не подходит.
          sessionStorage.removeItem(PENDING_SUPPLIER_INVITE_KEY);
        }

        // Перенаправляем на соответствующую страницу по роли
        this.redirectByRole(response.user);
      },
      error: (err) => {
        this.loading = false;
        if (err.status === 401 || err.status === 403) {
          this.error = this.languageService.translate('login.errors.invalid');
        } else if (err.error && err.error.message) {
          this.error = err.error.message;
        } else {
          this.error = this.languageService.translate('login.errors.default');
        }
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) {
        return this.languageService.translate('login.errors.required');
      }
      if (field.errors['minlength']) {
        return this.languageService.translate('login.errors.minlength', {
          min: field.errors['minlength'].requiredLength
        });
      }
      if (field.errors['maxlength']) {
        return this.languageService.translate('login.errors.maxlength', {
          max: field.errors['maxlength'].requiredLength
        });
      }
    }
    return '';
  }

  private redirectByRole(user?: UserDto | null): void {
    // Если пользователь передан напрямую (после логина), используем его
    // Иначе пытаемся получить из AuthService (для уже авторизованных)
    const userToCheck = user || this.authService.getUser();
    
    if (!userToCheck || !userToCheck.role) {
      this.router.navigate(['/']);
      return;
    }

    const roleCode = userToCheck.role.code;
    switch (roleCode) {
      case 'ROLE_CARGO_OWNER':
        this.router.navigate(['/cargo-owner/dashboard']);
        break;
      case 'ROLE_FREIGHT_FORWARDER':
        this.router.navigate(['/freight-forwarder/dashboard']);
        break;
      case 'ROLE_ADMIN':
        this.router.navigate(['/admin/users']);
        break;
      case 'ROLE_PUBLIC':
        this.router.navigate(['/public/dashboard']);
        break;
      case 'ROLE_SUPPLIER':
        this.router.navigate(['/supplier/dashboard']);
        break;
      default:
        this.router.navigate(['/']);
        break;
    }
  }
}
